// CP2020 armor LAYER SORTING — the prerequisite to both the effective-SP stacking and the
// EV (encumbrance) calculations. This owns ONE thing: given a character's worn armor, decide
// which pieces apply at a location, in what INNER→OUTER order, for a given calculation — and
// whether the whole loadout is even a legal configuration. It does NOT do the SP/EV math; the
// calculators consume this ordering.
//
//   const sorter = new ArmorLayerSorter(pieces)   // validates the WHOLE loadout, once
//   if (!sorter.valid) { /* render "invalid armor" — see invalidReason */ }
//   const layers = sorter.layersFor("torso", "sp")  // ordered OUTER→INNER; [] if invalid
//
// A PIECE is a plain object (decoupled from the Stimulus item — the caller adapts):
//   { sp: Number, weight: "light"|"medium"|"heavy",
//     isHard: Bool, isSkinWeave: Bool, isIntegral: Bool, isCover: Bool,
//     locations: string[] | Set }   // body locations this piece covers
//
// POSITION signals decide layer order (checked innermost-first; cover outranks all):
//   isCover     → the cover entry (a wall/terrain the target is behind). Always OUTERMOST
//                 (outside all worn armor). EXCLUDED from every validity gate (terrain isn't
//                 worn armor), but PRESENT in the SP/breach stack (it protects + degrades).
//   isSkinWeave → innermost, but EJECTED from the EV layer set ("counts as a layer, receives
//                 no penalty"); INCLUDED (innermost) for SP. Also EXEMPT from the max-layer
//                 count entirely.
//   isIntegral  → innermost, and COUNTS as a layer (dermal/bodyplating — built-in, not
//                 worn-over). In both SP and EV sets.
//   isHard      → OUTERMOST (among worn armor). (Cyberlimb is the one exception; DEFERRED.)
//   else        → ordinary worn armor, sorted between the pins by weight then SP.
//
// VALIDITY is a property of the WHOLE loadout, checked at EVERY location (not just the one
// asked for) and INDEPENDENT of mode — a loadout is legal or it isn't. Any location failing
// any gate invalidates the whole thing, and the sorter refuses to produce orderings (fail-
// loud, never silently drop a layer). Computed ONCE in the constructor.

export const WEIGHTS = { light: 0, medium: 1, heavy: 2 }
const HEAD = "head"
const MAX_LAYERS = { head: 2, default: 3 } // per location: head caps at 2, everywhere else 3

// The composite sort key, inner→outer ascending: [cover, innermost, outermost, weight, sp].
// Lexicographic on the tuple — earlier slots outrank later ones. The position flags are
// INDEPENDENT and ORDERED innermost-first, NOT collapsed into one tier — this is deliberate:
//   - cover     = 1 when it's the cover entry (isCover), else 0. HIGHEST-precedence slot, so
//                 cover always sorts OUTERMOST (a wall/terrain the target is behind sits outside
//                 ALL worn armor — first thing damage hits). Beats even a hard worn layer.
//   - innermost = 0 when skinweave/integral (so it sorts first), else 1.
//   - outermost = 1 when hard (so it sorts last), else 0.
// Because innermost outranks outermost, a piece that is innermost sorts to the inside REGARDLESS
// of its outermost flag. So body plating (integral AND hard) stays INNERMOST — its isHard only
// ranks it among the *non-innermost* pieces (and never does, since it's innermost). isHard on
// such a piece therefore means "rigid material" (for the max-1-hard validity gate + weapon
// rules), NOT "outermost position"; the key precedence handles that without any special case.
// Cover is the ONE thing that outranks innermost — it's outermost unconditionally. Within a
// position, lighter then lower-SP sort innermost.
function sortKey(piece) {
  const cover = piece.isCover ? 1 : 0
  const innermost = piece.isSkinWeave || piece.isIntegral ? 0 : 1
  const outermost = piece.isHard ? 1 : 0
  return [cover, innermost, outermost, WEIGHTS[piece.weight] ?? WEIGHTS.medium, Number(piece.sp) || 0]
}

function tupleCompare(a, b) {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] - b[i]
  }
  return 0
}

function covers(piece, location) {
  const locs = piece.locations
  return locs instanceof Set ? locs.has(location) : Array.isArray(locs) && locs.includes(location)
}

const isSkinweave = (p) => !!p.isSkinWeave
const isIntegral = (p) => !!p.isIntegral
const isHard = (p) => !!p.isHard
// Cover (a wall/terrain the target is behind) is passed as an armor-shaped entry flagged
// isCover:true (id is typically "cover" for its degradation handle). It's EXCLUDED from all
// validity gates (it's terrain, not worn armor — a wall + your hard armor isn't "two hard
// layers") but is PRESENT in the SP/breach stack (it protects + degrades) and always sorts
// OUTERMOST. Regular pieces have no isCover, so read false — same absence-is-false convention
// as isHard/isSkinWeave/isIntegral.
const isCover = (p) => !!p.isCover

export class ArmorLayerSorter {
  // Validate the WHOLE loadout up front — every location any piece covers is gated, so an
  // illegal leg stack makes even a torso query invalid. valid/invalidReason are computed once.
  constructor(pieces) {
    this.pieces = Array.isArray(pieces) ? pieces : []
    this._validate()
  }

  // Ordered pieces OUTER→INNER at `location` for `mode` ("sp" | "ev"). Returns an EMPTY ARRAY
  // when the loadout is invalid (so callers can iterate unconditionally — invalid means "no
  // layers apply"; check .valid to render the invalid state distinctly). Mode's ONLY effect:
  // "ev" ejects skinweave entirely; "sp" keeps it (pinned innermost). Filtering + ordering are
  // per-location; validity is not.
  //
  // ORDER: outermost first. The sortKey ranks inner→outer ascending (low = innermost), so we
  // sort then REVERSE. Outer→inner is the canonical direction across the damage system — damage
  // physically enters from outside, breach thresholds accumulate outer→inner, and the SP-stack
  // grows outer→inner. (armor_ev is order-independent — count + internal re-sort — so the flip
  // is transparent to it; its tests confirm.)
  layersFor(location, mode) {
    if (!this.valid) return []
    let here = this.pieces.filter((p) => covers(p, location))
    if (mode === "ev") here = here.filter((p) => !isSkinweave(p))
    return here.slice().sort((a, b) => tupleCompare(sortKey(a), sortKey(b))).reverse()
  }

  // === validity =================================================================
  // Gates, per location, mode-independent (a loadout is legal or not):
  //   1. max layers: head 2 / else 3, where the count = integral + worn (skinweave EXEMPT).
  //   2. max 1 innermost per location (skinweave + integral combined).
  //   3. max 1 hard per location.
  // COVER is invisible to ALL gates (terrain, not worn armor) — filtered out up front. First
  // failure sets valid=false + invalidReason (location + which gate). Fail-loud.
  _validate() {
    this.valid = true
    this.invalidReason = null

    for (const location of this._locations()) {
      const here = this.pieces.filter((p) => covers(p, location) && !isCover(p))

      // (1) max layers — skinweave doesn't count toward the cap.
      const counted = here.filter((p) => !isSkinweave(p)).length
      const cap = MAX_LAYERS[location] ?? MAX_LAYERS.default
      if (counted > cap) return this._invalid(location, `too many layers (${counted} > ${cap})`)

      // (2) at most one innermost (skinweave + integral).
      const innermost = here.filter((p) => isSkinweave(p) || isIntegral(p)).length
      if (innermost > 1) return this._invalid(location, `multiple innermost pieces (${innermost})`)

      // (3) at most one hard.
      const hard = here.filter(isHard).length
      if (hard > 1) return this._invalid(location, `multiple hard-armor pieces (${hard})`)
    }
  }

  _invalid(location, detail) {
    this.valid = false
    this.invalidReason = { location, detail }
  }

  // Every location any piece touches — the validity gates run over all of them, not just a
  // location a caller happens to ask about.
  _locations() {
    const set = new Set()
    for (const p of this.pieces) {
      const locs = p.locations instanceof Set ? p.locations : Array.isArray(p.locations) ? p.locations : []
      for (const l of locs) set.add(l)
    }
    return set
  }
}

// Exported for tests / potential reuse.
export { sortKey, HEAD, MAX_LAYERS, isCover }
